/**
 * Factory functions for creating implicit FeedbackEvent objects.
 *
 * Each function returns a properly typed FeedbackEvent with type='implicit'.
 * The `detail` field carries structured context as a human-readable string
 * (consumed later by the AI digest).
 */

import { FeedbackEvent, ImplicitAction } from '../redux/feedbackSlice';

/** Generate a unique event ID */
const eventId = (): string =>
  `fb-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/** Base factory — shared fields for all implicit events */
const createImplicitEvent = (
  action: ImplicitAction,
  weekStartDate: string,
  fields: Partial<Pick<FeedbackEvent, 'recipeId' | 'recipeName' | 'detail'>>,
): FeedbackEvent => ({
  id: eventId(),
  type: 'implicit',
  action,
  date: new Date().toISOString(),
  weekStartDate,
  ...(fields.recipeId !== undefined ? { recipeId: fields.recipeId } : {}),
  ...(fields.recipeName !== undefined ? { recipeName: fields.recipeName } : {}),
  ...(fields.detail !== undefined ? { detail: fields.detail } : {}),
});

// ---- Public factories ----

/** User dragged/moved a meal to a different day or meal type */
export const createSwapEvent = (
  weekStartDate: string,
  recipeId: string,
  recipeName: string,
  fromDate: string,
  toDate: string,
  fromMealType: string,
  toMealType: string,
): FeedbackEvent =>
  createImplicitEvent('swap', weekStartDate, {
    recipeId,
    recipeName,
    detail: `Moved from ${fromDate} ${fromMealType} to ${toDate} ${toMealType}`,
  });

/** User changed the servings for a meal */
export const createServingsChangeEvent = (
  weekStartDate: string,
  recipeId: string,
  recipeName: string,
  oldServings: number,
  newServings: number,
): FeedbackEvent =>
  createImplicitEvent('servings_change', weekStartDate, {
    recipeId,
    recipeName,
    detail: `Servings changed from ${oldServings} to ${newServings}`,
  });

/** Plan was regenerated — this event is emitted once per regeneration */
export const createSilentRegenEvent = (
  weekStartDate: string,
  oldMealCount: number,
  newMealCount: number,
): FeedbackEvent =>
  createImplicitEvent('silent_regen', weekStartDate, {
    detail: `Regenerated plan: ${oldMealCount} meals replaced with ${newMealCount} new meals`,
  });

/** A meal survived a full plan regeneration (user didn't change it) */
export const createKeptThroughRegenEvent = (
  weekStartDate: string,
  recipeId: string,
  recipeName: string,
  regenCount: number,
): FeedbackEvent =>
  createImplicitEvent('kept_through_regen', weekStartDate, {
    recipeId,
    recipeName,
    detail: `Kept through ${regenCount} regeneration(s)`,
  });

/** User confirmed the plan with few or no edits */
export const createQuickConfirmEvent = (
  weekStartDate: string,
  mealCount: number,
  changeCount: number,
): FeedbackEvent =>
  createImplicitEvent('quick_confirm', weekStartDate, {
    detail: `Confirmed ${mealCount} meals with ${changeCount} change(s)`,
  });

/** User confirmed the plan after many edits */
export const createHeavyEditConfirmEvent = (
  weekStartDate: string,
  mealCount: number,
  changeCount: number,
): FeedbackEvent =>
  createImplicitEvent('heavy_edit_confirm', weekStartDate, {
    detail: `Confirmed ${mealCount} meals after ${changeCount} change(s)`,
  });

/** User replaced a generated meal with a free-text entry */
export const createReplacedWithFreetextEvent = (
  weekStartDate: string,
  replacedRecipeId: string,
  replacedRecipeName: string,
  freeText: string,
): FeedbackEvent =>
  createImplicitEvent('replaced_with_freetext', weekStartDate, {
    recipeId: replacedRecipeId,
    recipeName: replacedRecipeName,
    detail: `Replaced with free-text: "${freeText}"`,
  });

/** User marked a meal as cooked/done */
export const createMealCompletedEvent = (
  weekStartDate: string,
  recipeId: string,
  recipeName: string,
  servings: number,
): FeedbackEvent =>
  createImplicitEvent('meal_completed', weekStartDate, {
    recipeId,
    recipeName,
    detail: `Marked as done (${servings} servings)`,
  });

/** User rated a completed meal */
export const createMealRatedEvent = (
  weekStartDate: string,
  recipeId: string,
  recipeName: string,
  rating: number,
  chips: string[],
  comment?: string,
): FeedbackEvent => {
  const parts = [`Rated ${rating}/5`];
  if (chips.length > 0) {
    parts.push(`Feedback: ${chips.join(', ')}`);
  }
  if (comment) {
    parts.push(`Comment: "${comment}"`);
  }
  return createImplicitEvent('meal_rated', weekStartDate, {
    recipeId,
    recipeName,
    detail: parts.join('. '),
  });
};
